import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AreaSelect from '../components/AreaSelect';
import { routeApi, driverApi, escortApi } from '../api/mockApi';

const VEHICLE_TYPES = ['Saloon Car', 'Wheelchair Minibus', 'Black Cab', '7 Seater MPV', '9 Seater MPV', 'W/C Taxi'];
const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];
const DEFAULT_RADIUS = 2;
const NEAREST_LIMIT = 8;

const RouteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [nearest, setNearest] = useState({ radiusMiles: DEFAULT_RADIUS, drivers: [], escorts: [] });
  const [loadingNearest, setLoadingNearest] = useState(true);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [radiusInput, setRadiusInput] = useState(String(DEFAULT_RADIUS));
  const [showAssign, setShowAssign] = useState(null); // 'driver' | 'escort' | null
  const [candidates, setCandidates] = useState({ drivers: [], escorts: [] });
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const loadRoute = useCallback(async () => {
    const res = await routeApi.getById(id);
    if (res.success) {
      setRoute(res.data);
      setForm(res.data);
    }
  }, [id]);

  const loadNearest = useCallback(async () => {
    setLoadingNearest(true);
    const res = await routeApi.nearest(id, radius, NEAREST_LIMIT);
    if (res.success) {
      setNearest({
        radiusMiles: res.data?.radiusMiles ?? radius,
        drivers: res.data?.drivers || [],
        escorts: res.data?.escorts || [],
      });
    }
    setLoadingNearest(false);
  }, [id, radius]);

  useEffect(() => { loadRoute(); }, [loadRoute]);
  useEffect(() => { loadNearest(); }, [loadNearest]);

  const reload = async () => {
    await Promise.all([loadRoute(), loadNearest()]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Never send back the server-computed location fields: they would make the
    // server keep the old coordinates instead of re-geocoding the new post code.
    const { latitude, longitude, driverName, escortName, covered, ...rest } = form;
    const postCodeMoved = (form.postCode || '') !== (route.postCode || '');
    const areaEdited = (form.area || '') !== (route.area || '');
    const payload = {
      ...rest,
      // A moved post code invalidates the inherited area unless the admin typed
      // a new one, so clear it and let the server derive it again.
      area: postCodeMoved && !areaEdited ? '' : (form.area || ''),
    };
    const res = await routeApi.update(id, payload);
    if (res.success) {
      setRoute(res.data);
      setForm(res.data);
      setEditing(false);
      loadNearest();
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = route.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE';
    const res = await routeApi.update(id, { routeStatus: newStatus });
    if (res.success) {
      setRoute(res.data);
      setForm(res.data);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this route?')) {
      await routeApi.delete(id);
      navigate('/routes');
    }
  };

  // The modal must offer everyone, not just the geocoded people the radius
  // search returns, so load the full list when it opens.
  const openAssign = async (kind) => {
    setShowAssign(kind);
    setLoadingCandidates(true);
    const res = kind === 'driver' ? await driverApi.getAll({}) : await escortApi.getAll({});
    const list = res.success ? (res.data || []) : [];
    setCandidates(prev => (kind === 'driver' ? { ...prev, drivers: list } : { ...prev, escorts: list }));
    setLoadingCandidates(false);
  };

  const handleAssign = async (kind, personId) => {
    const body = kind === 'driver' ? { driverId: personId } : { escortId: personId };
    const res = await routeApi.assign(id, body);
    if (res.success) setShowAssign(null);
    reload();
  };

  const handleUnassign = async (kind) => {
    const body = kind === 'driver' ? { driverId: true } : { escortId: true };
    await routeApi.unassign(id, body);
    reload();
  };

  const handleApplyRadius = () => {
    const parsed = parseFloat(radiusInput);
    if (isNaN(parsed) || parsed <= 0) {
      setRadiusInput(String(radius));
      return;
    }
    if (parsed === radius) {
      loadNearest();
      return;
    }
    setRadius(parsed);
  };

  if (!route) return <div className="loading">Loading...</div>;

  const assignedDriver = nearest.drivers.find(d => d.id === route.assignedDriver);
  const assignedEscort = nearest.escorts.find(e => e.id === route.assignedEscort);
  const driversWithin = nearest.drivers.filter(d => d.withinRadius);
  const escortsWithin = nearest.escorts.filter(e => e.withinRadius);

  const formatMiles = (miles) => `${Number(miles || 0).toFixed(1)} mi`;

  // Everyone free for this route, with the measured distance merged in where the
  // person has coordinates; people without a location stay assignable.
  const buildModalList = () => {
    const isDriver = showAssign === 'driver';
    const measured = isDriver ? nearest.drivers : nearest.escorts;
    const measuredById = {};
    measured.forEach(p => { measuredById[p.id] = p; });
    const pool = (isDriver ? candidates.drivers : candidates.escorts)
      .filter(p => !p.routeId || p.routeId === route.id);
    return (pool.length ? pool : measured)
      .map(p => (measuredById[p.id]
        ? { ...p, ...measuredById[p.id] }
        : { ...p, distanceMiles: null, withinRadius: false }))
      .sort((a, b) => {
        if (a.distanceMiles == null && b.distanceMiles == null) {
          return String(a.fullName || '').localeCompare(String(b.fullName || ''));
        }
        if (a.distanceMiles == null) return 1;
        if (b.distanceMiles == null) return -1;
        return a.distanceMiles - b.distanceMiles;
      });
  };

  const modalList = showAssign ? buildModalList() : [];

  const renderNearestItem = (person, kind) => {
    const isAssigned = kind === 'driver'
      ? person.id === route.assignedDriver
      : person.id === route.assignedEscort;
    const sub = [person.area, person.postCode, person.vehicle].filter(Boolean).join(' · ');
    return (
      <div key={person.id} className="nearest-item">
        <div className="who">
          <span className="nearest-name">{person.fullName || 'N/A'}</span>
          <span className="nearest-sub">{sub || person.mobileNumber || 'No location details'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`distance-badge ${person.withinRadius ? 'within' : 'outside'}`}>
            {person.distanceMiles == null ? 'Location unknown' : formatMiles(person.distanceMiles)}
          </span>
          {isAssigned ? (
            <span className="pill">Assigned</span>
          ) : (
            <button className="btn btn-gold btn-sm" onClick={() => handleAssign(kind, person.id)}>Assign</button>
          )}
        </div>
      </div>
    );
  };

  const renderNearestList = (list, kind, withinCount) => {
    if (!list.length) return <p className="empty-state">No candidates with a known location.</p>;
    return (
      <>
        {!withinCount && (
          <p className="empty-state">
            Nobody within {nearest.radiusMiles} miles — nearest is {formatMiles(list[0].distanceMiles)} away
            {list[0].area ? ` in ${list[0].area}` : ''}.
          </p>
        )}
        {list.map(p => renderNearestItem(p, kind))}
      </>
    );
  };

  return (
    <div>
      <Link to="/routes" className="back-link">← Back to Routes</Link>

      <div className="page-header">
        <h1 className="page-title">{route.name || 'Route Details'}</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={handleStatusToggle}>
            Switch to {route.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE'}
          </button>
          <button className="btn btn-gold btn-sm" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>Route Details</h3>
              <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{route.name || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{route.area || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Post Code</span><span className="detail-value">{route.postCode || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Vehicle Required</span><span className="detail-value">{route.vehicleRequired || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Created</span><span className="detail-value">{route.createdAt || 'N/A'}</span></div>
            </div>
            <div className="detail-card">
              <h3>Location</h3>
              {route.latitude != null && route.longitude != null ? (
                <div>
                  <div className="detail-row"><span className="detail-label">Latitude</span><span className="detail-value">{Number(route.latitude).toFixed(5)}</span></div>
                  <div className="detail-row"><span className="detail-label">Longitude</span><span className="detail-value">{Number(route.longitude).toFixed(5)}</span></div>
                </div>
              ) : (
                <p className="empty-state">Not geocoded — add a post code</p>
              )}
            </div>
            <div className="detail-card">
              <h3>Status</h3>
              <span className={`status-badge ${route.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`} style={{ fontSize: 16, padding: '8px 20px' }}>
                <span className="dot"></span>
                {route.routeStatus}
              </span>
            </div>
            <div className="detail-card">
              <h3>Assigned Driver</h3>
              {route.driverName ? (
                <div>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{route.driverName}</span></div>
                  <div className="detail-row"><span className="detail-label">Mobile</span><span className="detail-value">{assignedDriver?.mobileNumber || 'N/A'}</span></div>
                </div>
              ) : (
                <p className="empty-state">No driver assigned</p>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-outline btn-sm" onClick={() => openAssign('driver')}>
                  {route.driverName ? 'Change driver' : 'Assign driver'}
                </button>
                {route.assignedDriver && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleUnassign('driver')}>Unassign</button>
                )}
              </div>
            </div>
            <div className="detail-card">
              <h3>Assigned PA</h3>
              {route.escortName ? (
                <div>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{route.escortName}</span></div>
                  <div className="detail-row"><span className="detail-label">Mobile</span><span className="detail-value">{assignedEscort?.mobileNumber || 'N/A'}</span></div>
                </div>
              ) : (
                <p className="empty-state">No PA assigned</p>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-outline btn-sm" onClick={() => openAssign('escort')}>
                  {route.escortName ? 'Change PA' : 'Assign PA'}
                </button>
                {route.assignedEscort && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleUnassign('escort')}>Unassign</button>
                )}
              </div>
            </div>
            <div className="detail-card">
              <h3>Notes</h3>
              {route.notes ? (
                <p style={{ color: '#666666', fontSize: 14, whiteSpace: 'pre-wrap' }}>{route.notes}</p>
              ) : (
                <p className="empty-state">No notes</p>
              )}
            </div>
          </div>

          <h3 className="section-title">Nearest Available</h3>

          <div className="filters-bar">
            <div className="filter-field" style={{ flex: '0 0 auto' }}>
              <label>Search Radius (miles)</label>
              <div className="radius-control">
                <input
                  type="number"
                  min="0.5"
                  max="25"
                  step="0.5"
                  value={radiusInput}
                  onChange={e => setRadiusInput(e.target.value)}
                />
                <button type="button" className="btn btn-outline btn-sm" onClick={handleApplyRadius}>Apply</button>
              </div>
            </div>
            <div className="filters-actions">
              <span className="filter-summary">
                {loadingNearest
                  ? 'Searching...'
                  : `${driversWithin.length} driver${driversWithin.length === 1 ? '' : 's'} and ${escortsWithin.length} PA${escortsWithin.length === 1 ? '' : 's'} within ${nearest.radiusMiles} miles`}
              </span>
            </div>
          </div>

          {loadingNearest ? (
            <div className="loading">Finding nearest available...</div>
          ) : (
            <div className="coverage-grid">
              <div className={`coverage-card ${driversWithin.length ? 'ok' : ''}`}>
                <h4>Nearest Drivers</h4>
                <p className="coverage-meta">
                  Unassigned drivers, closest first, measured from {route.postCode || 'the route location'}.
                </p>
                {renderNearestList(nearest.drivers, 'driver', driversWithin.length)}
              </div>
              <div className={`coverage-card ${escortsWithin.length ? 'ok' : ''}`}>
                <h4>Nearest Passenger Assistants</h4>
                <p className="coverage-meta">
                  Unassigned PAs, closest first, measured from {route.postCode || 'the route location'}.
                </p>
                {renderNearestList(nearest.escorts, 'escort', escortsWithin.length)}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="modal" style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3>Edit Route</h3>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Name</label>
              <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Area</label>
              <AreaSelect
                  value={form.area || ''}
                  onChange={v => setForm({ ...form, area: v })}
                  blankLabel="Detect from post code"
                />
            </div>
            <div className="form-group">
              <label>Post Code</label>
              <input value={form.postCode || ''} onChange={e => setForm({ ...form, postCode: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Vehicle Required</label>
              <select value={form.vehicleRequired || ''} onChange={e => setForm({ ...form, vehicleRequired: e.target.value })}>
                <option value="">Select vehicle</option>
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Route Status</label>
              <select value={form.routeStatus || 'OFF ROUTE'} onChange={e => setForm({ ...form, routeStatus: e.target.value })}>
                {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea rows={3} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => { setForm(route); setEditing(false); }}>Cancel</button>
              <button type="submit" className="btn btn-gold">Save</button>
            </div>
          </form>
        </div>
      )}

      {showAssign && (
        <div className="modal-overlay" onClick={() => setShowAssign(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{showAssign === 'driver' ? 'Assign Driver' : 'Assign Passenger Assistant'}</h3>
            <p className="coverage-meta">
              Everyone available for this route, closest first. Distances are measured from{' '}
              {route.postCode || route.area || 'this route'}.
            </p>
            {loadingCandidates ? (
              <p className="empty-state">Loading candidates...</p>
            ) : modalList.length ? (
              modalList.map(p => renderNearestItem(p, showAssign))
            ) : (
              <p className="empty-state">
                No available {showAssign === 'driver' ? 'drivers' : 'passenger assistants'} to assign.
              </p>
            )}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAssign(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetailPage;
